import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import HowItWorks from './pages/HowItWorks';
import LocationsList from './pages/LocationsList';
import LocationDetail from './pages/LocationDetail';
import FeaturedLocations from './pages/FeaturedLocations';
import FeaturedLocationDetail from './pages/FeaturedLocationDetail';
import Onboarding from './pages/Onboarding';
import ForRestaurants from './pages/ForRestaurants';
import RequestLocation from './pages/RequestLocation';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import Checkout from './pages/Checkout';
import BookingSuccess from './pages/BookingSuccess';
import BookingDetail from './pages/BookingDetail';
import InviteRespond from './pages/InviteRespond';
import Dashboard from './pages/dashboard/Dashboard';
import EmailPreview from './pages/dashboard/EmailPreview';
import Protected from './components/Protected';

export default function App() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<HomeOrDashboard />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/featured-locations" element={<FeaturedLocations />} />
      <Route path="/featured-locations/:slug" element={<FeaturedLocationDetail />} />
      {/* /locations and /locations/:slug are gated: live system is signed-in only. */}
      <Route
        path="/locations"
        element={
          <Protected>
            <LocationsList />
          </Protected>
        }
      />
      <Route
        path="/locations/:slug"
        element={
          <Protected>
            <LocationDetail />
          </Protected>
        }
      />
      <Route path="/bars" element={<Navigate to="/featured-locations" replace />} />
      <Route path="/bars/:slug" element={<RedirectBar />} />
      <Route path="/for-restaurants" element={<ForRestaurants />} />
      <Route path="/for-bars" element={<Navigate to="/for-restaurants" replace />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/request-location" element={<RequestLocation />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/:token" element={<InviteRespond />} />
      {/* Public preview gallery for the transactional email system. Same component
          the platform dashboard exposes at /dashboard/emails for signed-in admins,
          mounted publicly so the team and external reviewers can share a link
          without each needing an admin account. Renders templates from sample
          data — no real bookings or PII touched. */}
      <Route path="/preview/emails" element={<EmailPreviewShell />} />
      <Route
        path="/checkout/:offerId"
        element={
          <Protected>
            <Checkout />
          </Protected>
        }
      />
      <Route
        path="/bookings/:id/success"
        element={
          <Protected>
            <BookingSuccess />
          </Protected>
        }
      />
      <Route
        path="/bookings/:id"
        element={
          <Protected>
            <BookingDetail />
          </Protected>
        }
      />
      <Route
        path="/dashboard/*"
        element={
          <Protected>
            <Dashboard />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function EmailPreviewShell() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '32px 24px 96px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <EmailPreview />
      </div>
    </div>
  );
}

function RedirectBar() {
  const slug = window.location.pathname.split('/').pop();
  return <Navigate to={`/featured-locations`} state={{ slug }} replace />;
}

// Signed-in users skip the marketing page and land on their dashboard.
// Marketing pages (/how-it-works, /for-restaurants, /featured-locations) stay
// accessible whether signed in or not so users can still browse them.
function HomeOrDashboard() {
  const { loading, session } = useAuth();
  if (loading) {
    // Brief, neutral loading state. Avoids a flash of the marketing page
    // before the redirect kicks in.
    return <div style={{ minHeight: '100vh', background: 'var(--bg)' }} />;
  }
  if (session) return <Navigate to="/dashboard" replace />;
  return <Home />;
}
